import type { FieldValues, UseFormSetError, Path } from 'react-hook-form';
import { ApiError, NetworkError } from '@/lib/api-client';

/**
 * Routes an API failure to the right place: field-level errors go onto the
 * matching inputs, and anything without a field becomes the form-level
 * message. Returns that message, or null when everything was field-specific.
 */
export function applyApiError<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  knownFields: readonly string[],
): string {
  if (error instanceof NetworkError) return error.message;

  if (error instanceof ApiError) {
    let matchedAny = false;
    for (const detail of error.details) {
      if (knownFields.includes(detail.path)) {
        setError(detail.path as Path<T>, { type: 'server', message: detail.message });
        matchedAny = true;
      }
    }
    // If every detail mapped onto a field, the inputs already say it all.
    return matchedAny && error.details.length > 0 ? '' : error.message;
  }

  return 'Something went wrong. Please try again.';
}

/**
 * Pulls out the errors that belong to custom fields, keyed by api name.
 *
 * They cannot go through `applyApiError`: the form's field list is fixed at
 * compile time and these fields are defined by the organization at runtime, so
 * `setError('customFields.territory')` has no input to attach to.
 */
export function customFieldErrorsFrom(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError)) return {};

  const prefix = 'customFields.';
  return Object.fromEntries(
    error.details
      .filter((detail) => detail.path.startsWith(prefix))
      .map((detail) => [detail.path.slice(prefix.length), detail.message]),
  );
}
